/**
 * Command-palette open state + recent-items persistence (OPSFL-POLISH-1).
 *
 * Modelled on `help/helpPanelStore.ts`: a tiny external store rather than context, because the
 * palette is mounted ONCE in the shell and must be openable from anywhere (a global hotkey, a
 * TopBar button, a future in-page affordance) with no provider threading and no new dependency.
 *
 * Recents live in localStorage — navigation history only (surface ids, flow ids, entity ids the
 * operator already saw on screen). No PII beyond identifiers the operator just typed, no tokens:
 * CLAUDE.md non-negotiable #1 stays intact.
 */
import { useSyncExternalStore } from 'react';
import { parseRecents, pushRecent, RECENTS_KEY, type RecentItem } from './paletteModel';

let open = false;
const listeners = new Set<() => void>();

function emit() {
  for (const l of listeners) l();
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function openPalette(): void {
  if (!open) {
    open = true;
    emit();
  }
}

export function closePalette(): void {
  if (open) {
    open = false;
    emit();
  }
}

export function togglePalette(): void {
  open = !open;
  emit();
}

export function usePaletteOpen(): boolean {
  return useSyncExternalStore(subscribe, () => open);
}

// ---- recents -----------------------------------------------------------------------------------

let recentsCache: RecentItem[] | null = null;
const recentListeners = new Set<() => void>();

function readRecents(): RecentItem[] {
  if (recentsCache) return recentsCache;
  try {
    recentsCache = parseRecents(
      typeof localStorage === 'undefined' ? null : localStorage.getItem(RECENTS_KEY),
    );
  } catch {
    recentsCache = []; // storage disabled (private mode / hardened browser) — never fatal
  }
  return recentsCache;
}

/** Record a chosen item as most-recent. Silent no-op when storage is unavailable. */
export function recordRecent(item: RecentItem): void {
  const next = pushRecent(readRecents(), item);
  recentsCache = next;
  try {
    localStorage.setItem(RECENTS_KEY, JSON.stringify(next));
  } catch {
    /* non-fatal: the ring still works for this tab */
  }
  for (const l of recentListeners) l();
}

export function useRecents(): RecentItem[] {
  return useSyncExternalStore(
    (l) => {
      recentListeners.add(l);
      return () => recentListeners.delete(l);
    },
    readRecents,
  );
}

/** Test seam: drop the in-memory cache so a fresh localStorage value is re-read. */
export function resetRecentsCache(): void {
  recentsCache = null;
}
